/**
 * Restyled Lead row — used inside the "Current Leads" tab.
 *
 * What's inline (no dialog):
 *   • Strength up/down arrows next to the colored strength bar
 *   • Status tag chips (Showing set / BG sent / BG done / Lease sent /
 *     Lease signed / + custom). Click to toggle. Long-press / popover
 *     editing handled by the parent's status popover (kept simple here).
 *   • "Move to Quick" arrow button
 *   • Delete button (with inline confirm)
 *
 * Click anywhere else on the row → calls onOpenDetail(lead) which the parent
 * uses to open the LeadFormDialog in view-mode (with an Edit button inside
 * the dialog header).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ChevronUp, ChevronDown, ArrowRightLeft, Trash2, DollarSign, Calendar, Plus, X, Check, StickyNote, UserPlus, Phone, Mail, MessageSquare } from 'lucide-react';
import { logLeadContact } from '@/lib/api';
import { toast } from 'sonner';

function relTime(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const diff = Date.now() - then.getTime();
  const day = 1000 * 60 * 60 * 24;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))}w ago`;
  return `${Math.floor(diff / (30 * day))}mo ago`;
}

const CONTACT_CHANNELS = [
  { channel: 'email', field: 'emailed_dates', label: 'Email', Icon: Mail, color: 'text-sky-700 hover:bg-sky-50' },
  { channel: 'call', field: 'called_dates', label: 'Call', Icon: Phone, color: 'text-emerald-700 hover:bg-emerald-50' },
  { channel: 'text', field: 'texted_dates', label: 'Text', Icon: MessageSquare, color: 'text-violet-700 hover:bg-violet-50' },
];

export const STRENGTH_COLORS = {
  1: { row: 'bg-red-50/60', bar: 'bg-red-500', text: 'text-red-900', label: 'Weak' },
  2: { row: 'bg-orange-50/70', bar: 'bg-orange-500', text: 'text-orange-900', label: 'Fair' },
  3: { row: 'bg-yellow-50/70', bar: 'bg-yellow-500', text: 'text-yellow-900', label: 'Good' },
  4: { row: 'bg-emerald-50/70', bar: 'bg-emerald-500', text: 'text-emerald-900', label: 'Strong' },
};

export const STATUS_PRESETS = [
  { tag: 'showing_set', label: 'Showing set', color: 'bg-sky-100 text-sky-900 border-sky-300' },
  { tag: 'bg_sent', label: 'BG Check sent', color: 'bg-violet-100 text-violet-900 border-violet-300' },
  { tag: 'bg_done', label: 'BG Check done', color: 'bg-indigo-100 text-indigo-900 border-indigo-300' },
  { tag: 'lease_sent', label: 'Lease sent', color: 'bg-amber-100 text-amber-900 border-amber-300' },
  { tag: 'lease_signed', label: 'Lease signed', color: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
];
const TAG_TO_PRESET = Object.fromEntries(STATUS_PRESETS.map((p) => [p.tag, p]));

const stop = (e) => { e.stopPropagation(); };

function StatusChip({ status, displayLabel, color, onSaveNote, onRemove }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(status.note || '');
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { stop(e); setOpen(true); setDraft(status.note || ''); }}
          className={`group inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border tabular-nums ${color}`}
          data-testid={`status-chip-${status.tag}-${status.id}`}
        >
          <Check className="h-2.5 w-2.5" /> {displayLabel}
          {status.note && <StickyNote className="h-2.5 w-2.5 opacity-70" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" onClick={stop} align="start">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Note</p>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Optional note for this status…"
          className="h-20 text-xs"
          data-testid={`status-note-${status.tag}-${status.id}`}
        />
        <div className="flex items-center justify-between mt-2">
          <Button variant="ghost" size="sm" className="h-7 text-[11px] text-destructive hover:bg-destructive/10" onClick={() => { onRemove(); setOpen(false); }}>
            <Trash2 className="h-3 w-3 mr-1" /> Remove
          </Button>
          <Button size="sm" className="h-7 text-[11px]" onClick={() => { onSaveNote(draft); setOpen(false); }}>Save</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AddStatusButton({ existingTags, onAdd }) {
  const [open, setOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={stop}
          className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50"
          data-testid="lead-add-status-btn"
        >
          <Plus className="h-2.5 w-2.5" /> Status
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" onClick={stop} align="start">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 px-1">Add status</p>
        <div className="flex flex-col gap-1">
          {STATUS_PRESETS.map((p) => {
            const taken = existingTags.includes(p.tag);
            return (
              <button
                key={p.tag}
                disabled={taken}
                onClick={() => { onAdd({ tag: p.tag }); setOpen(false); }}
                className={`text-left text-xs px-2 py-1 rounded ${taken ? 'opacity-40 cursor-not-allowed' : 'hover:bg-slate-50'}`}
                data-testid={`lead-add-preset-${p.tag}`}
              >
                {p.label}
              </button>
            );
          })}
          <div className="border-t pt-1.5 mt-1">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 px-1">Custom</p>
            <div className="flex gap-1 px-1">
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && customLabel.trim()) { onAdd({ tag: 'custom', label: customLabel.trim() }); setCustomLabel(''); setOpen(false); } }}
                placeholder="Label…"
                className="h-7 text-xs"
                data-testid="lead-custom-status-input"
              />
              <Button size="sm" className="h-7 text-[11px] px-2" disabled={!customLabel.trim()} onClick={() => { onAdd({ tag: 'custom', label: customLabel.trim() }); setCustomLabel(''); setOpen(false); }}>Add</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LeadRow({
  lead, unitMap,
  onOpenDetail, onSetStrength, onUpdateStatuses, onMoveToQuick, onDelete, onConvert, onUpdated,
}) {
  const strength = STRENGTH_COLORS[lead.lead_strength] || STRENGTH_COLORS[1];
  const unitNames = (lead.potential_unit_ids || [])
    .map((uid) => unitMap[uid]?.unit_number)
    .filter(Boolean);
  const statuses = lead.statuses || [];
  const existingTags = statuses.map((s) => s.tag);
  const showingsCount = (lead.showings || []).length;
  const [confirmDel, setConfirmDel] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);
  const lastContactRel = relTime(lead.last_contact_date);

  const logContact = async (channel) => {
    setBusyChannel(channel);
    try {
      await logLeadContact(lead.id, channel);
      onUpdated && onUpdated();
    } catch { toast.error('Failed to log contact'); }
    finally { setBusyChannel(null); }
  };

  const bumpStrength = (delta) => {
    const next = Math.max(1, Math.min(4, (lead.lead_strength || 1) + delta));
    if (next !== lead.lead_strength) onSetStrength(lead.id, next);
  };

  const addStatus = (entry) => {
    const next = [...statuses, { ...entry, id: undefined, note: '', set_at: new Date().toISOString() }];
    onUpdateStatuses(lead.id, next);
  };
  const updateStatusNote = (statusId, note) => {
    onUpdateStatuses(lead.id, statuses.map((s) => s.id === statusId ? { ...s, note } : s));
  };
  const removeStatus = (statusId) => {
    onUpdateStatuses(lead.id, statuses.filter((s) => s.id !== statusId));
  };

  return (
    <div
      className={`relative flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer hover:bg-slate-50/50 ${strength.row}`}
      data-testid="leads-table-row"
      onClick={() => onOpenDetail && onOpenDetail(lead)}
    >
      <div className={`absolute left-0 top-0 h-full w-1 rounded-l ${strength.bar}`} />

      {/* Strength up/down arrows (inline) */}
      <div className="pl-2 flex flex-col items-center justify-center mr-2">
        <button
          type="button"
          onClick={(e) => { stop(e); bumpStrength(1); }}
          disabled={(lead.lead_strength || 1) >= 4}
          className="h-4 w-4 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30 flex items-center justify-center"
          title="Stronger"
          data-testid={`strength-up-${lead.id}`}
        >
          <ChevronUp className="h-3 w-3" />
        </button>
        <span className={`text-[9px] font-semibold ${strength.text} my-px`}>{strength.label}</span>
        <button
          type="button"
          onClick={(e) => { stop(e); bumpStrength(-1); }}
          disabled={(lead.lead_strength || 1) <= 1}
          className="h-4 w-4 rounded hover:bg-slate-200 text-slate-600 disabled:opacity-30 flex items-center justify-center"
          title="Weaker"
          data-testid={`strength-down-${lead.id}`}
        >
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{lead.name}</span>
          {lead.source && <span className="text-xs text-muted-foreground">via {lead.source}</span>}
          {lead.price_offered && (
            <Badge variant="outline" className="text-[10px] gap-0.5 tabular-nums">
              <DollarSign className="h-2.5 w-2.5" />{parseFloat(lead.price_offered).toLocaleString()}
            </Badge>
          )}
          {lead.desired_start_date && lead.desired_end_date && (
            <span className="text-xs text-muted-foreground">{lead.desired_start_date} — {lead.desired_end_date}</span>
          )}
          {unitNames.length > 0 && (
            <div className="flex gap-1">
              {unitNames.map((n, i) => (
                <Badge key={i} variant="secondary" className="text-[10px]">{n}</Badge>
              ))}
            </div>
          )}
          {showingsCount > 0 && (
            <Badge variant="outline" className="text-[10px] gap-0.5">
              <Calendar className="h-2.5 w-2.5" />{showingsCount} showing{showingsCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Status chips row */}
        <div className="flex items-center gap-1 flex-wrap mt-1.5">
          {statuses.map((s) => {
            const preset = TAG_TO_PRESET[s.tag];
            const displayLabel = preset ? preset.label : (s.label || 'Custom');
            const color = preset ? preset.color : 'bg-slate-100 text-slate-800 border-slate-300';
            return (
              <StatusChip
                key={s.id}
                status={s}
                displayLabel={displayLabel}
                color={color}
                onSaveNote={(note) => updateStatusNote(s.id, note)}
                onRemove={() => removeStatus(s.id)}
              />
            );
          })}
          <AddStatusButton existingTags={existingTags} onAdd={addStatus} />
        </div>

        {/* Contact-tracking cluster — mirrors the QuickLeadRow layout so
            admins get the same Email / Call / Text log affordances on the
            Current Leads page. */}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {CONTACT_CHANNELS.map(({ channel, field, label, Icon, color }) => {
            const count = (lead[field] || []).length;
            return (
              <button
                key={channel}
                type="button"
                onClick={(e) => { stop(e); logContact(channel); }}
                disabled={busyChannel === channel}
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-slate-200 ${color} disabled:opacity-50`}
                title={`Log a ${label.toLowerCase()} today`}
                data-testid={`lead-row-log-${channel}-${lead.id}`}
              >
                <Icon className="h-2.5 w-2.5" />
                <span>{label}</span>
                {count > 0 && <span className="font-semibold tabular-nums">·{count}</span>}
              </button>
            );
          })}
          {lastContactRel && (
            <span className="text-[10px] text-slate-500">
              Last contact <span className="font-medium tabular-nums">{lastContactRel}</span>
            </span>
          )}
        </div>

        {lead.notes && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{lead.notes}</p>
        )}
      </div>

      {/* Right-side actions */}
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        {!lead.converted_to_tenant && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-slate-600 hover:bg-slate-100" onClick={(e) => { stop(e); onConvert(lead); }} title="Convert to tenant" data-testid={`lead-convert-${lead.id}`}>
            <UserPlus className="h-3 w-3" />
          </Button>
        )}
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-slate-600 hover:bg-slate-100" onClick={(e) => { stop(e); onMoveToQuick(lead.id); }} title="Move to Quick Leads" data-testid={`lead-move-quick-${lead.id}`}>
          <ArrowRightLeft className="h-3 w-3" />
        </Button>
        {confirmDel ? (
          <div className="flex items-center gap-0.5">
            <Button size="sm" variant="destructive" className="h-7 text-[11px] px-2" onClick={(e) => { stop(e); setConfirmDel(false); onDelete(lead.id); }} data-testid={`lead-del-yes-${lead.id}`}>Delete</Button>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2" onClick={(e) => { stop(e); setConfirmDel(false); }}><X className="h-3 w-3" /></Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={(e) => { stop(e); setConfirmDel(true); }} data-testid={`lead-del-${lead.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
